import { type ChangeEvent, useRef } from 'react';
import { useExportModalActions } from '~/hooks/useExportModalActions';
import { useAlert } from '~/systems/alert';
import { useExperiment } from '~/systems/experiment';
import { useHelp } from '~/systems/help';
import { useTitleRef } from '~/systems/title';
import IconButton from '../IconButton/IconButton';
import NewExperimentModal from '../NewExperimentModal/NewExperimentModal';
import './sidebarContent.css';

interface SidebarProps {
  isCreatingNewExperiment: boolean;
}

const sideButtonProps = {
  background: 'hover' as const,
  padding: 10,
  size: 20,
  square: true
};

export const TitleIconButton = ({
  title,
  icon,
  label,
  disabled,
  onClick
}: {
  title: string;
  icon: string;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
}) => {
  const ref = useTitleRef<HTMLButtonElement>(title);
  return (
    <IconButton
      ref={ref}
      icon={icon}
      label={label}
      {...sideButtonProps}
      disabled={disabled}
      onClick={onClick}
    />
  );
};

export const SidebarContent = ({ isCreatingNewExperiment }: SidebarProps) => {
  const experiment = useExperiment();
  const alertSys = useAlert();
  const help = useHelp();
  const fileInput = useRef<HTMLInputElement>(null);

  const {
    exportPulserSequence,
    openConfirmOverwriteModal,
    handleCancelOverwrite,
    handleConfirmOverwrite,
    isConfirmOverwriteModalVisible
  } = useExportModalActions();

  function newExperiment() {
    experiment.clear();
    if (fileInput.current) {
      fileInput.current.value = '';
    }
  }

  async function onImportFile(e: ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    if (input.files?.length === 1) {
      const file = input.files[0];
      const text = await file.text();
      try {
        const error = experiment.load(text);
        if (error) {
          alertSys.setAlert(error);
        }
      } catch (e) {
        alertSys.setAlert((e as Error).message);
      }
    }
  }

  function navigateToPulserDocs() {
    window.open('https://docs.pasqal.com/pulserstudio', '_blank');
  }

  function triggerImportFile() {
    fileInput.current?.click();
  }

  return (
    <>
      <TitleIconButton
        title="New experiment"
        icon="add-hexagon"
        label="New experiment"
        disabled={isCreatingNewExperiment}
        onClick={() => openConfirmOverwriteModal(newExperiment)}
      />
      <TitleIconButton
        title="Import experiment"
        icon="import"
        label="Import experiment"
        disabled={isCreatingNewExperiment}
        onClick={() => openConfirmOverwriteModal(triggerImportFile)}
      />
      <input ref={fileInput} type="file" hidden onChange={onImportFile} />
      <TitleIconButton
        title="Export experiment"
        icon="upload"
        label="Export experiment"
        disabled={isCreatingNewExperiment}
        onClick={exportPulserSequence}
      />
      <TitleIconButton
        title="Product tour"
        icon="help"
        label="Product tour"
        disabled={isCreatingNewExperiment}
        onClick={() => help.trigger('init')}
      />
      <TitleIconButton
        title="Pulser Studio documentation"
        icon="page-search"
        label="Pulser Studio documentation"
        onClick={navigateToPulserDocs}
      />
      <NewExperimentModal
        isModalVisible={isConfirmOverwriteModalVisible}
        onHandleContinue={() => handleConfirmOverwrite(true)}
        onHandleContinueWithoutSaving={() => handleConfirmOverwrite(false)}
        onClose={handleCancelOverwrite}
        body={
          <>
            Last modifications haven't been saved yet. Export before diving into
            the next experiment…
          </>
        }
      />
    </>
  );
};
